<template>
  <template v-for="item in items" :key="item.id">
    <li
      v-if="anchorLinks && item.type === 'code'"
      class="nav-item"
    >
      <b-button
        type="button"
        variant="primary"
        class="page-menu-button"
        @click="runItemCode(item)"
      >{{ item.label || 'Run' }}</b-button>
    </li>

    <li
      v-else-if="anchorLinks && depth === 1 && item.type === 'link'"
      class="nav-item"
    >
      <b-button
        v-if="showsAsButton(item)"
        type="button"
        variant="primary"
        class="page-menu-button"
        @click="followLink(item)"
      >{{ item.label }}</b-button>
      <router-link v-else class="page-menu-link" :to="pageTo(item)">{{ item.label }}</router-link>
    </li>

    <b-nav-item
      v-else-if="depth === 1 && item.type === 'link'"
      :to="pageTo(item)"
      :active="currentPage === item.pageUniqueTitle"
    >{{ item.label }}</b-nav-item>

    <b-nav-item-dropdown
      v-else-if="depth === 1 && isMenu(item)"
      class="menu-branch"
      :text="item.type === 'dropdown' ? item.label : undefined"
    >
      <template v-if="item.type === 'value'" #button-content>
        <span v-if="item.label">{{ item.label }}</span>
        <span class="menu-value-badge">{{ valueText(item) }}</span>
      </template>
      <MenuEntries
        :items="childrenOf(item)"
        :depth="depth + 1"
        :application="application"
        :current-page="currentPage"
        :value-text="valueText"
        :children-of="childrenOf"
        :link-path="linkPath"
        :anchor-links="anchorLinks"
      />
    </b-nav-item-dropdown>

    <li
      v-else-if="anchorLinks && depth === 1 && roleFor(item) === 'link'"
      class="nav-item"
    >
      <router-link class="page-menu-link menu-value-link" :to="pageTo(item)">
        <span v-if="item.label">{{ item.label }}</span>
        <span class="menu-value-badge">{{ valueText(item) }}</span>
      </router-link>
    </li>

    <b-nav-item
      v-else-if="depth === 1 && roleFor(item) === 'link'"
      class="menu-value-link"
      :to="pageTo(item)"
      :active="currentPage === item.pageUniqueTitle"
    >
      <span v-if="item.label">{{ item.label }}</span>
      <span class="menu-value-badge">{{ valueText(item) }}</span>
    </b-nav-item>

    <span v-else-if="depth === 1 && roleFor(item) === 'text'" class="menu-value">
      <span v-if="item.label">{{ item.label }}</span>
      <span class="menu-value-badge">{{ valueText(item) }}</span>
    </span>

    <div
      v-else-if="roleFor(item) === 'submenu'"
      class="menu-submenu"
      :class="{ open: isOpen(item.id) }"
      @mouseenter="showMenu(item.id)"
      @mouseleave="hideMenu(item.id)"
      @focusin="showMenu(item.id)"
      @focusout="onMenuFocusOut($event, item.id)"
    >
      <button
        type="button"
        class="dropdown-item menu-submenu-toggle"
        :aria-expanded="isOpen(item.id) ? 'true' : 'false'"
        aria-haspopup="menu"
        @mousedown.stop
        @click.stop.prevent="toggleMenu(item.id)"
        @keydown.escape.stop.prevent="closeMenu(item.id)"
      >
        <span v-if="item.label">{{ item.label }}</span>
        <span v-if="item.type === 'value'" class="menu-value-badge">{{ valueText(item) }}</span>
        <span class="menu-submenu-caret" aria-hidden="true">›</span>
      </button>
      <div class="dropdown-menu menu-submenu-panel">
        <MenuEntries
          :items="childrenOf(item)"
          :depth="depth + 1"
          :application="application"
          :current-page="currentPage"
          :value-text="valueText"
          :children-of="childrenOf"
          :link-path="linkPath"
          :anchor-links="anchorLinks"
        />
      </div>
    </div>

    <li v-else-if="anchorLinks && (roleFor(item) === 'page' || roleFor(item) === 'link')">
      <b-button
        v-if="showsAsButton(item)"
        type="button"
        variant="primary"
        class="page-menu-button"
        @click="followLink(item)"
      >{{ item.label }}</b-button>
      <router-link v-else class="page-menu-link" :to="pageTo(item)">
        <template v-if="item.type === 'value'">
          <span v-if="item.label">{{ item.label }}</span>
          <span class="menu-value-badge">{{ valueText(item) }}</span>
        </template>
        <template v-else>{{ item.label }}</template>
      </router-link>
    </li>

    <b-dropdown-item
      v-else-if="roleFor(item) === 'page' || roleFor(item) === 'link'"
      :to="pageTo(item)"
      :active="currentPage === item.pageUniqueTitle"
    >
      <template v-if="item.type === 'value'">
        <span v-if="item.label">{{ item.label }}</span>
        <span class="menu-value-badge">{{ valueText(item) }}</span>
      </template>
      <template v-else>{{ item.label }}</template>
    </b-dropdown-item>

    <span
      v-else-if="roleFor(item) === 'text'"
      class="dropdown-item menu-value-text"
    >
      <span v-if="item.label">{{ item.label }}</span>
      <span class="menu-value-badge">{{ valueText(item) }}</span>
    </span>
  </template>
</template>

<script>
import menuValueRole from '../menu-value-role.js'
import { menuLinkUsesButton } from '../../../shared/page-menu.js'
import { runCustomCodeInPage } from '../lib/run-custom-code'

export default {
  name: 'MenuEntries',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    depth: {
      type: Number,
      required: true
    },
    application: {
      type: String,
      default: ''
    },
    currentPage: {
      type: String,
      default: ''
    },
    linkPath: {
      type: Function,
      default: null
    },
    anchorLinks: {
      type: Boolean,
      default: false
    },
    valueText: {
      type: Function,
      required: true
    },
    childrenOf: {
      type: Function,
      required: true
    }
  },
  data () {

    return { hovered: null, pinned: {}, runningCode: {} }
  },
  methods: {
    isOpen (id) {

      return !!(this.pinned[id] || this.hovered === id)
    },
    showMenu (id) {

      this.hovered = id
    },
    hideMenu (id) {

      if (this.hovered === id) this.hovered = null
    },
    onMenuFocusOut (event, id) {

      const next = event.relatedTarget

      if (next && event.currentTarget.contains(next)) return

      this.hideMenu(id)
    },
    closeMenu (id) {

      this.pinned[id] = false
      this.hideMenu(id)
    },
    toggleMenu (id) {

      this.pinned[id] = !this.pinned[id]
    },
    pageTo (item) {

      if (this.linkPath) return this.linkPath(item)

      return { path: `/${this.application}/${item.pageUniqueTitle}` }
    },
    showsAsButton (item) {

      return menuLinkUsesButton(item)
    },
    async runItemCode (item) {

      const code = item && typeof item.code === 'string' ? item.code : ''

      if (!code.trim() || this.runningCode[item.id]) return

      const label = String(item.label || 'button').replace(/[\\/\0]/g, '').trim().slice(0, 48) || 'button'

      this.runningCode[item.id] = true

      try {

        await runCustomCodeInPage({
          files: [{ uniqueTitle: 'menu_button', name: `${label}.js`, code }]
        })
      } catch (error) {

        console.error(`Custom code ${label} failed and was skipped.`, error)
      } finally {

        this.runningCode[item.id] = false
      }
    },
    followLink (item) {

      const to = this.pageTo(item)

      if (!to || !to.path || !this.$router) return

      this.$router.push(to).catch(() => {})
    },
    roleFor (item) {

      return menuValueRole.menuEntryRole(item, this.depth, this.childrenOf(item).length)
    },
    isMenu (item) {

      const role = this.roleFor(item)

      return role === 'dropdown' || role === 'submenu'
    }
  }
}
</script>

<style>
.dropdown-menu,
.dropdown-menu.overflow-auto {
  overflow: visible !important;
}

.menu-submenu {
  position: relative;
}

.menu-value {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.9);
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
}

.menu-value-link .nav-link,
.menu-branch .nav-link,
a.page-menu-link.menu-value-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.dropdown-menu > li > a.page-menu-link {
  display: block;
  width: 100%;
  padding: 0.25rem 1rem;
  clear: both;
  white-space: nowrap;
  background: transparent;
  border: 0;
}

.menu-value-badge {
  display: inline-block;
  margin-left: 6px;
  border: 1px solid currentColor;
  border-radius: 999px;
  padding: 0 7px;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.4;
}

.menu-submenu-toggle > .menu-value-badge,
.menu-value > .menu-value-badge,
.menu-value-link .nav-link > .menu-value-badge,
a.page-menu-link.menu-value-link > .menu-value-badge,
.menu-branch .dropdown-toggle > .menu-value-badge {
  margin-left: 0;
}

.dropdown-item .menu-value-badge {
  margin-left: 6px;
}

.menu-submenu-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.menu-submenu-caret {
  margin-left: auto;
}

.menu-value-text {
  cursor: default;
}

.menu-submenu-panel {
  display: none;
  position: absolute;
  top: 0;
  left: 100%;
  min-width: 10rem;
}

.menu-submenu.open > .menu-submenu-panel,
.menu-submenu:hover > .menu-submenu-panel {
  display: block;
}
</style>
